/**
 * Service worker registration, and the update loop that keeps a tab from running
 * last week's code.
 *
 * Production-only: when the app is served from localhost (Vite dev), the SW would
 * cache stale HMR chunks.
 *
 * WHAT THE OLD COMMENT HERE GOT WRONG, AND WHAT IT COST
 * ----------------------------------------------------
 * It said: "On install of a new version, the SW calls skipWaiting() and
 * clients.claim() so users always pick up the latest deploy on the next page
 * load — no ghost old build issue."
 *
 * They do not. `skipWaiting()` + `clients.claim()` make the new worker take
 * CONTROL of open pages. They never reload those pages, so the page keeps running
 * the JavaScript it already parsed. What actually happens on a deploy is worse
 * than a stale tab:
 *
 *   - the new SW's `activate` deletes every cache not matching the new build hash,
 *     so the chunks the running page was built against are gone from the cache;
 *   - Pages serves only the current deployment, so they are gone from the network
 *     too;
 *   - the page then navigates to a lazy route and the chunk 404s.
 *
 * That is where "route chunk for PracticeTestReport loaded without its export"
 * came from, nine times, on a student's finished practice test. The claim is what
 * turned a harmless stale tab into a broken one.
 *
 * It is also why "The database connection is closing" kept being reported for
 * days after its fix shipped: nothing ever moved those tabs onto the fixed code,
 * so they went on reporting a bug that no longer existed and every triage pass
 * had to re-derive that they were stale.
 *
 * THE LOOP
 * --------
 * 1. Ask for an update whenever the tab becomes visible (throttled). A single
 *    page app does not navigate, so without this an installed PWA can go a long
 *    time without ever checking.
 * 2. When a new worker takes control, reload — but only once the page is HIDDEN,
 *    so the student never sees it happen. A tab that is reloaded while they are
 *    reading is a worse bug than the one being fixed.
 *
 * The reload is deliberately the whole point: after it, the page and the worker
 * are the same build, which is the only state in which the cache strategy is
 * sound. lib/chunk-reload.ts covers the window before that, where a claimed tab
 * is running old code against purged caches.
 */

import { isSafeToReplaceApp } from "./app-busy.js";
import { markUpdateAvailable } from "./app-update.js";
import { checkStale } from "./build-staleness.js";
import { buildId } from "./build-id.js";
import { reportFunnel } from "./report-error.js";

/** At most one update check per this interval, per page. */
const UPDATE_CHECK_MS = 15 * 60 * 1000;

export function registerServiceWorker(): void {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;

  const host = window.location.hostname;
  const isLocal =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".local");

  if (isLocal) {
    // Aggressively unregister if any old SW survived; otherwise cached chunks
    // break the dev loop.
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => undefined);
    return;
  }

  // Whether a worker has EVER been in control of this page. The first claim of a
  // previously-uncontrolled page also fires `controllerchange`, and reloading on
  // that one would reload every first visit.
  //
  // This was a `const` captured at load, which meant a page that started
  // uncontrolled ignored every later controllerchange for its whole lifetime —
  // so a first visit that stayed open across a deploy, which is exactly the long
  // session this loop exists to rescue, would never update. It has to be a `let`
  // that flips after the first claim is adopted.
  let hasBeenControlled = !!navigator.serviceWorker.controller;
  let updatePending = false;

  // A reload is safe only when nothing is in progress that it would damage. See
  // lib/app-busy.ts: "the tab is hidden" is NOT that property, because on mobile
  // hidden also means the student switched apps or locked the screen mid-question.
  const applyUpdateWhenSafe = () => {
    if (!updatePending) return;
    if (!isSafeToReplaceApp()) return;
    // Still prefer the background so the reload is invisible, but only as a
    // nicety on top of the safety gate, never as a substitute for it.
    if (document.visibilityState !== "hidden") return;
    updatePending = false;
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hasBeenControlled) {
      // First-ever claim: adopt it, do not reload, and from here on treat any
      // further controller change as a real replacement.
      hasBeenControlled = true;
      return;
    }
    updatePending = true;
    // Tell the UI as well. If the silent path can take it, it will and the
    // banner never appears; if the student is mid-drill, asking is the only way
    // they ever get off the stale build.
    markUpdateAvailable();
    applyUpdateWhenSafe();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        let lastCheck = Date.now();
        // A pending update also becomes applicable when the student LEAVES a
        // drill or test, which is a route change rather than a visibility
        // change. Without this, an update that arrived mid-drill would wait for
        // the next backgrounding that happens to occur outside one.
        window.addEventListener("popstate", applyUpdateWhenSafe);
        window.addEventListener("pageshow", applyUpdateWhenSafe);
        // POPSTATE IS NOT A ROUTE CHANGE IN THIS APP. It fires for back and
        // forward only; React Router navigates with history.pushState, which
        // fires nothing. So the comment above was true of the intent and false
        // of the code: tapping "Home" out of a drill, the single most common way
        // a student leaves a critical route, did not re-check.
        //
        // That is how a tab stays stale indefinitely. `/app/drill/` is a critical
        // prefix, so isSafeToReplaceApp() refuses while the student is on it, and
        // the only recheck was a browser-back that phone users rarely use. app-busy
        // reasons that "a missed update costs nothing, the next cold launch picks
        // it up", which is exactly wrong for a parked tab that never cold-launches.
        //
        // Measured: 104 IndexedDB "connection is closing" rejections from
        // /app/drill/slice carrying NO build field at all, which means a bundle
        // older than 2026-09-10 when that field shipped, which is older than the
        // 2026-09-08 fix for the error they are reporting. The fix held; the tabs
        // never got it.
        for (const m of ["pushState", "replaceState"] as const) {
          const orig = history[m];
          history[m] = function (this: History, ...args: Parameters<History["pushState"]>) {
            const r = orig.apply(this, args);
            // After the URL has changed, so isSafeToReplaceApp() reads the route
            // the student is arriving at rather than the one they are leaving.
            window.dispatchEvent(new Event("bht:navigated"));
            return r;
          };
        }
        window.addEventListener("bht:navigated", applyUpdateWhenSafe);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "hidden") {
            applyUpdateWhenSafe();
            return;
          }
          if (Date.now() - lastCheck < UPDATE_CHECK_MS) return;
          lastCheck = Date.now();
          // Never let a failed update check surface: it is entirely routine
          // offline, and this runs on a page the student is using.
          reg.update().catch(() => undefined);
          // AND ask the site directly. reg.update() is the service worker
          // checking itself, which is exactly the thing that can be stuck:
          // production had students on a build days old, running wholly from
          // service-worker cache, whose assets no longer existed on the CDN.
          // Nothing could tell them, because controllerchange never fired.
          // This compares the running build against what the site actually
          // serves, so a stuck worker cannot hide the answer.
          void checkStale(buildId()).then((stale) => {
            if (stale) {
              reportFunnel("stale_build_detected");
              markUpdateAvailable();
              applyUpdateWhenSafe();
            }
          });
        });
      })
      .catch((err) => {
        // Swallow — SW registration failure shouldn't break the app.
        console.warn("[sw] registration failed", err);
      });
  });
}
